import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import apiFetch from "../../../../shared/services/apiClient";
import { parseMoney, sanitizeMoneyTyping } from "../../../../shared/utils/parseMoney";
import { useMultimoneda } from "../../../../shared/hooks/useMultimoneda";
import { useTipoCambio } from "../../services/useTipoCambio";
import styles from "../styles/addReceipt.module.css";
import SendReceiptEmailModal from "../components/SendReceiptEmailModal";

import { useReceipt } from "../hooks/useAddReceipt";
import { useCxc } from "../hooks/useCxc";
import { usePendingReceipts } from "../hooks/useReceipt";
import { ReceiptDetails } from "../components/Add/ReceiptDetails";
import { CxcDetails } from "../components/Add/CxcDetails";
import { ReceiptFooter } from "../components/Add/ReceiptFooter";
import PendingReceiptsTable from "../../shared/PendingReceiptsTable";
import DecimalInput from "../../../../shared/components/DecimalInput";

export default function AddReceipt() {
  const receipt = useReceipt();
  // useCxc receives bank's currency and TC_compra (not receipt moneda_pago)
  const cxc = useCxc(
    receipt.receiptData.monto,
    receipt.selectedFilial,
    receipt.setCount,
    receipt.banco_moneda,         // from selected bank account's moneda
    receipt.receiptData.tipo_cambio, // TC compra for the day
  );
  const [displayValueReceipt, setDisplayValueReceipt] = useState("");
  const [depositoMode, setDepositoMode] = useState(false);
  const [depositoForm, setDepositoForm] = useState({
    monto: "", fecha_pago: "", metodo_pago: "TRANSFERENCIA",
    documento: "", descripcion: "", cuenta_bancaria: "",
    tipo_cambio: "", moneda_pago: "CRC",
  });
  const [depositoError, setDepositoError] = useState("");
  const [depositoSaving, setDepositoSaving] = useState(false);

  // Estado para el correo — igual que AddInvoices
  const [lastCreated, setLastCreated] = useState(null); // recibo recién creado
  const [emailModal, setEmailModal] = useState(null); // array de recibos | null

  const [pendingKey, setPendingKey] = useState(0);
  const { pending } = usePendingReceipts(pendingKey);

  return (
    <>
          <span className={styles.inactive}>
            <Link to="/">
              <span className={styles.inactive}>Inicio /</span>
            </Link>
            <Link to="/finanzas/cxc">
              <span className={styles.inactive}> Cuentas por Cobrar /</span>
            </Link>
            <Link to="/finanzas/recibos">
              <span className={styles.inactive}> Recibos /</span>
            </Link>
            <span className={styles.active}> Agregar</span>
          </span>
          
      <h2 className="mt-2 me-2 p-0 title">Generar Recibo</h2>

      {/* ── Banner de éxito — IGUAL QUE INVOICES ─────────────────────── */}
      {lastCreated && (
        <div
          style={{
            background: "rgba(74,222,128,0.08)",
            border: "1px solid rgba(74,222,128,0.3)",
            borderLeft: "4px solid #4ade80",
            borderRadius: 8,
            padding: "12px 18px",
            marginBottom: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          <span
            style={{ color: "#4ade80", fontWeight: 500, fontSize: "0.9rem" }}
          >
            <i
              className="fa-solid fa-circle-check"
              style={{ marginRight: 8 }}
            />
            Recibo {lastCreated.numero_recibo} creado correctamente.
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => setEmailModal([lastCreated])}
              style={{
                padding: "6px 14px",
                background: "rgba(249,115,22,0.15)",
                border: "1px solid rgba(249,115,22,0.4)",
                borderRadius: 7,
                color: "#f97316",
                fontSize: "0.83rem",
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <i className="fa-solid fa-envelope" />
              Enviar correo al residente
            </button>
            <button
              onClick={() => setLastCreated(null)}
              style={{
                background: "none",
                border: "none",
                color: "#4ade80",
                cursor: "pointer",
                fontSize: "1rem",
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* ── Modal de correo ────────────────────────────────────────────── */}
      {emailModal && (
        <SendReceiptEmailModal
          recibos={emailModal}
          onClose={() => {
            setEmailModal(null);
            setLastCreated(null);
          }}
        />
      )}

      {/* ── Pendientes ─────────────────────────────────────────────────── */}
      <PendingReceiptsTable
        pendientes={pending}
        pageSize={5}
        title={
          pending.length > 0
            ? `Tienes ${pending.length} recibo(s) sin facturas aplicadas — aplícalos antes de continuar`
            : undefined
        }
      />

      {/* ── Toggle Depósito sin identificar ─────────────────────────── */}
      <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          type="button"
          onClick={() => { setDepositoMode(v => !v); setDepositoError(''); }}
          style={{
            padding: '6px 16px',
            background: depositoMode ? 'rgba(249,115,22,0.15)' : 'var(--hover-bg)',
            border: `1px solid ${depositoMode ? 'rgba(249,115,22,0.5)' : 'var(--border-card)'}`,
            borderRadius: 7, color: depositoMode ? '#f97316' : 'var(--text-secondary)',
            fontSize: '0.83rem', fontWeight: 600, cursor: 'pointer',
          }}
        >
          <i className={`fa-solid fa-${depositoMode ? 'toggle-on' : 'toggle-off'}`} style={{ marginRight: 6 }}/>
          Depósito sin identificar
        </button>
        {depositoMode && (
          <span style={{ color: '#f97316', fontSize: '0.78rem' }}>
            Se registrará en Pasivos → Depósitos sin Identificar
          </span>
        )}
      </div>

      {/* ── Formulario Depósito sin identificar ──────────────────────── */}
      {depositoMode ? (
        <DepositoSinIdentificarForm
          form={depositoForm}
          setForm={setDepositoForm}
          error={depositoError}
          setError={setDepositoError}
          saving={depositoSaving}
          setSaving={setDepositoSaving}
          onCreated={(r) => {
            setPendingKey(k => k + 1);
            setLastCreated(r);
            setDepositoMode(false);
            setDepositoForm({ monto:'', fecha_pago:'', metodo_pago:'TRANSFERENCIA', documento:'', descripcion:'', cuenta_bancaria:'', tipo_cambio:'', moneda_pago:'CRC' });
          }}
        />
      ) : (
      <div className={styles["receipt-container"]}>
        <ReceiptDetails
          {...receipt}
          displayValue={displayValueReceipt}
          setDisplayValue={setDisplayValueReceipt}
          onDistribute={cxc.distribute}
          selectedFilial={receipt.selectedFilial}
          setSelectedFilial={receipt.setSelectedFilial}
          readOnly={false}
          soloSaldoFavor={cxc.soloSaldoFavor}
          referenciaAutoSaldo={cxc.referenciaAutoSaldo}
          setBancoMoneda={receipt.setBancoMoneda}
        />
        <CxcDetails
          {...cxc}
          onDistribute={cxc.distribute}
          receiptMonto={receipt.receiptData.monto}
          bancoMoneda={receipt.banco_moneda}
          TC_compra={receipt.receiptData.tipo_cambio}
          metodoPago={receipt.receiptData.metodo_pago}
        />
        <ReceiptFooter
          {...receipt}
          {...cxc}
          setDisplayValueReceipt={setDisplayValueReceipt}
          // Pasar función de validación de monto (permite 0 si hay negativas)
          montoEsValido={cxc.montoEsValido}
          reloadCxc={cxc.reloadCxc}
          soloSaldoFavor={cxc.soloSaldoFavor}
          referenciaAutoSaldo={cxc.referenciaAutoSaldo}
          onCreated={async (recibo) => {
            // GET para obtener el objeto completo con filial.idFilial (igual que invoices)
            try {
              const full = await apiFetch(`/api/finance/recibo/${recibo.id}/`);
              setLastCreated(full.ok ? await full.json() : recibo);
            } catch (_) {
              setLastCreated(recibo);
            }
          }}
        />
      </div>
      )}
    </>
  );
}

// ── Formulario Depósito sin Identificar ──────────────────────────────────────
function DepositoSinIdentificarForm({ form, setForm, error, setError, saving, setSaving, onCreated }) {
  const [cuentas, setCuentas] = useState([]);
  const [displayMonto, setDisplayMonto] = useState("");
  const multimoneda = useMultimoneda();
  const { tipoCambio, loading: loadingTC } = useTipoCambio();

  // Auto-poblar TC de compra desde BCCR al cargar
  useEffect(() => {
    if (tipoCambio?.tipo_compra) {
      setForm(f => ({ ...f, tipo_cambio: Number(tipoCambio.tipo_compra) }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoCambio]);

  useEffect(() => {
    apiFetch('/api/finance/cuentaBancaria/')
      .then(r => r.json())
      .then(d => {
        const todas = d.results ?? d;
        // Sin multimoneda → solo cuentas CRC
        const list = multimoneda
          ? todas
          : todas.filter(c => (c.moneda || 'CRC').toUpperCase() === 'CRC');
        setCuentas(list);
        if (list.length > 0 && !form.cuenta_bancaria) {
          const first = list[0];
          setForm(f => ({
            ...f,
            cuenta_bancaria: String(first.id),
            moneda_pago: (first.moneda || 'CRC').toUpperCase(),
          }));
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multimoneda]);

  // Cuando cambia la cuenta bancaria, actualizar moneda_pago
  function handleBancoChange(e) {
    const id = e.target.value;
    const cuenta = cuentas.find(c => String(c.id) === id);
    setForm(f => ({
      ...f,
      cuenta_bancaria: id,
      moneda_pago: cuenta ? (cuenta.moneda || 'CRC').toUpperCase() : 'CRC',
      // TC se mantiene — se repoblará desde BCCR si el hook aún está cargado
    }));
  }

  const esUSD = form.moneda_pago === 'USD';
  const tcNum = Number(form.tipo_cambio) || 0;

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSave() {
    if (!form.monto || Number(form.monto) <= 0) { setError('El monto debe ser mayor a 0.'); return; }
    if (!form.fecha_pago) { setError('La fecha de pago es obligatoria.'); return; }
    if (!form.cuenta_bancaria) { setError('Seleccioná una cuenta bancaria.'); return; }
    if (!form.documento.trim()) { setError('La referencia bancaria es obligatoria.'); return; }
    if (!form.tipo_cambio || Number(form.tipo_cambio) <= 1) {
      setError('El tipo de cambio debe ser mayor a 1.');
      return;
    }
    setError(''); setSaving(true);
    try {
      const body = {
        monto:           Number(form.monto),
        fecha_pago:      form.fecha_pago,
        metodo_pago:     form.metodo_pago,
        documento:       form.documento,
        descripcion:     form.descripcion,
        cuenta_bancaria: Number(form.cuenta_bancaria),
        moneda_pago:     form.moneda_pago,
      };
      body.tipo_cambio = Number(form.tipo_cambio) || 1;
      const res = await apiFetch('/api/finance/recibo/deposito-sin-identificar/', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || JSON.stringify(data)); return; }
      onCreated(data);
    } catch (e) {
      setError('Error al guardar: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="receipt-container-deposito" style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      {/* Panel izquierdo — igual que ReceiptDetails */}
      <div style={{ flex: '1 1 340px', background: 'var(--bg-card)',
        border: '1px solid var(--border-card)', borderRadius: 12, padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20,
          paddingBottom: 16, borderBottom: '1px solid var(--border-card)' }}>
          <div style={{ background: 'rgba(249,115,22,0.15)', borderRadius: 8,
            padding: '8px 10px', color: '#f97316' }}>
            <i className="fa-solid fa-vault" style={{ fontSize: '1.1rem', color:"#f97316" }} />
          </div>
          <div>
            <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: '0.95rem' }}>Detalles del Depósito</div>
            <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>Sin filial identificada</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
          <div>
            <label style={{ display:'block', color:'var(--text-secondary)', fontSize:'0.78rem', fontWeight:600, marginBottom:4 }}>
              Fecha de Pago <span style={{color:'#f87171'}}>*</span>
            </label>
            <input type="date" value={form.fecha_pago}
              onChange={e => set('fecha_pago', e.target.value)}
              style={{ width:'100%', padding:'8px 12px', background:'var(--bg-input)',
                border:'1px solid var(--border-card)', borderRadius:7, color:'var(--text-primary)',
                fontSize:'0.88rem', colorScheme:'light dark' }}/>
          </div>
          <div>
            <label style={{ display:'block', color:'var(--text-secondary)', fontSize:'0.78rem', fontWeight:600, marginBottom:4 }}>
              Monto Total <span style={{color:'#f87171'}}>*</span>
            </label>
            <input type="text" value={displayMonto}
              onChange={e => {
                const raw = sanitizeMoneyTyping(e.target.value);
                setDisplayMonto(raw);
                set('monto', parseMoney(raw));
              }}
              onBlur={() => {
                const n = Number(form.monto);
                if (!isNaN(n) && n > 0) setDisplayMonto(n.toLocaleString('en-US', {minimumFractionDigits:2}));
              }}
              onFocus={() => setDisplayMonto(form.monto ? String(form.monto) : '')}
              placeholder="0.00"
              style={{ width:'100%', padding:'8px 12px', background:'var(--bg-input)',
                border:'1px solid var(--border-card)', borderRadius:7, color:'var(--text-primary)', fontSize:'0.88rem' }}/>
          </div>
          <div>
            <label style={{ display:'block', color:'var(--text-secondary)', fontSize:'0.78rem', fontWeight:600, marginBottom:4 }}>
              Medio de pago
            </label>
            <select value={form.metodo_pago} onChange={e => set('metodo_pago', e.target.value)}
              style={{ width:'100%', padding:'8px 12px', background:'var(--bg-input)',
                border:'1px solid var(--border-card)', borderRadius:7, color:'var(--text-primary)', fontSize:'0.88rem' }}>
              <option value="TRANSFERENCIA">Transferencia</option>
              <option value="EFECTIVO">Efectivo</option>
              <option value="CHEQUE">Cheque</option>
              <option value="TARJETA">Tarjeta</option>
            </select>
          </div>
          <div>
            <label style={{ display:'block', color:'var(--text-secondary)', fontSize:'0.78rem', fontWeight:600, marginBottom:4 }}>
              Cuenta Bancaria <span style={{color:'#f87171'}}>*</span>
            </label>
            <select value={form.cuenta_bancaria} onChange={handleBancoChange}
              style={{ width:'100%', padding:'8px 12px', background:'var(--bg-input)',
                border:'1px solid var(--border-card)', borderRadius:7, color:'var(--text-primary)', fontSize:'0.88rem' }}>
              <option value="">— Seleccionar —</option>
              {cuentas.map(c => (
                <option key={c.id} value={String(c.id)}>
                  {c.banco} — {c.numero_cuenta}{c.moneda === 'USD' ? ' (USD)' : ''}
                </option>
              ))}
            </select>
            {esUSD && (
              <div style={{ marginTop:4, fontSize:'0.72rem', color:'#f97316', fontWeight:600 }}>
                <i className="fa-solid fa-dollar-sign" style={{ marginRight:4 }}/>
                Cuenta en dólares — se requiere tipo de cambio
              </div>
            )}
          </div>
        </div>

        {/* Tipo de Cambio — siempre visible, auto-poblado desde BCCR */}
        <div style={{
          marginBottom: 14,
          border: '1px solid var(--border-card)', borderRadius: 10, overflow: 'hidden',
          background: 'rgba(255,255,255,0.02)',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 12px', borderBottom: '1px solid var(--border-card)',
            background: 'rgba(249,115,22,0.04)',
          }}>
            <i className="fa-solid fa-coins" style={{ color: '#f97316', fontSize: '0.7rem' }} />
            <span style={{ fontSize: '0.67rem', fontWeight: 700, color: 'var(--text-muted)',
              letterSpacing: '0.6px', textTransform: 'uppercase' }}>
              Tipo de Cambio de Referencia
            </span>
          </div>
          <div style={{ padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.73rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
              TC compra:
            </span>
            {loadingTC
              ? <i className="fa-solid fa-circle-notch fa-spin" style={{ color: '#60a5fa', fontSize: '0.7rem' }} />
              : <span style={{
                  fontSize: '0.64rem', fontWeight: 700,
                  background: `${'#4ade80'}18`, border: `1px solid ${'#4ade80'}45`,
                  borderRadius: 4, padding: '2px 7px', color: '#4ade80', letterSpacing: '0.3px',
                }}>BCCR</span>
            }
            <div style={{
              display: 'flex', alignItems: 'center', gap: 4,
              background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border-card)',
              borderRadius: 7, padding: '4px 10px 4px 9px',
            }}>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 600, lineHeight: 1 }}>₡</span>
              <DecimalInput
                value={form.tipo_cambio || ''}
                onChange={e => set('tipo_cambio', e.target.value)}
                style={{
                  width: 82, background: 'transparent', border: 'none',
                  color: 'var(--text-primary)', fontSize: '0.88rem',
                  fontFamily: 'monospace', fontWeight: 600, outline: 'none', padding: 0,
                }}
              />
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>/ USD</span>
            </div>
            {esUSD && tcNum > 1 && (
              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                ≈ ₡{(Number(form.monto || 0) * tcNum).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </span>
            )}
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display:'block', color:'var(--text-secondary)', fontSize:'0.78rem', fontWeight:600, marginBottom:4 }}>
            Referencia Bancaria <span style={{color:'#f87171'}}>*</span>
          </label>
          <input type="text" value={form.documento}
            onChange={e => set('documento', e.target.value)}
            placeholder="Número de transferencia o referencia bancaria"
            style={{ width:'100%', padding:'8px 12px', background:'var(--bg-input)',
              border:'1px solid var(--border-card)', borderRadius:7, color:'var(--text-primary)', fontSize:'0.88rem' }}/>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display:'block', color:'var(--text-secondary)', fontSize:'0.78rem', fontWeight:600, marginBottom:4 }}>
            Descripción <span style={{ color:'var(--text-muted)', fontWeight:400 }}>(opcional — anexo bancario)</span>
          </label>
          <input type="text" value={form.descripcion}
            onChange={e => set('descripcion', e.target.value)}
            placeholder="Texto para el anexo bancario"
            style={{ width:'100%', padding:'8px 12px', background:'var(--bg-input)',
              border:'1px solid var(--border-card)', borderRadius:7, color:'var(--text-primary)', fontSize:'0.88rem' }}/>
          <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:3 }}>
            Si se omite, se usa el N° de recibo automáticamente.
          </div>
        </div>

        {error && (
          <div style={{ background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)',
            borderRadius:7, padding:'8px 14px', color:'#f87171', fontSize:'0.84rem', marginBottom:12 }}>
            <i className="fa-solid fa-triangle-exclamation" style={{ marginRight:6 }}/>
            {error}
          </div>
        )}
      </div>

      {/* Panel derecho — info + botón guardar */}
      <div style={{ flex: '1 1 280px', background: 'var(--bg-card-alt)',
        border: '1px solid var(--border-card)', borderRadius: 12, padding: 24 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:20,
          paddingBottom:16, borderBottom:'1px solid var(--border-card)' }}>
          <div style={{ background:'rgba(99,102,241,0.15)', borderRadius:8,
            padding:'8px 10px', color:'#818cf8' }}>
            <i className="fa-solid fa-circle-info" style={{ fontSize:'1.1rem', color:'#818cf8' }}/>
          </div>
          <div style={{ color:'var(--text-primary)', fontWeight:700, fontSize:'0.95rem' }}>Registro Contable</div>
        </div>

        <div style={{ background:'rgba(249,115,22,0.07)', border:'1px solid rgba(249,115,22,0.2)',
          borderRadius:8, padding:'14px 16px', marginBottom:20, fontSize:'0.82rem', color:'#fdba74', lineHeight:1.6 }}>
          <div style={{ fontWeight:700, marginBottom:6 }}>Asiento generado:</div>
          <div>DEBE → Banco (Activo)</div>
          <div>HABER → Depósitos sin Identificar (Pasivo)</div>
          <div style={{ marginTop:10, color:'var(--text-secondary)', fontSize:'0.77rem' }}>
            Al aplicarlo a una filial se genera la contrapartida automáticamente.
          </div>
        </div>

        <div style={{ borderTop:'1px solid var(--border-card)', paddingTop:16, display:'flex',
          flexDirection:'column', gap:10 }}>
          <div style={{ display:'flex', justifyContent:'space-between', color:'var(--text-secondary)', fontSize:'0.82rem' }}>
            <span>Monto a registrar</span>
            <span style={{ color:'#f97316', fontWeight:700, fontSize:'1rem' }}>
              {esUSD
                ? `$${form.monto ? Number(form.monto).toFixed(2) : '0.00'}`
                : `₡${form.monto ? Number(form.monto).toLocaleString('en-US',{minimumFractionDigits:2}) : '0.00'}`
              }
            </span>
          </div>
          {esUSD && tcNum > 1 && (
            <div style={{ display:'flex', justifyContent:'space-between', color:'var(--text-muted)', fontSize:'0.78rem' }}>
              <span>Equivalente CRC (TC {tcNum})</span>
              <span style={{ color:'var(--text-secondary)', fontWeight:600 }}>
                ₡{(Number(form.monto || 0) * tcNum).toLocaleString('en-US',{minimumFractionDigits:2})}
              </span>
            </div>
          )}
          <button type="button" onClick={handleSave} disabled={saving}
            style={{ width:'100%', padding:'11px', background: saving ? 'rgba(249,115,22,0.5)' : '#f97316',
              border:'none', borderRadius:8, color:'#fff', fontWeight:700,
              fontSize:'0.9rem', cursor: saving ? 'not-allowed' : 'pointer',
              display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
            <i className="fa-solid fa-floppy-disk"/>
            {saving ? 'Guardando…' : 'Guardar Depósito'}
          </button>
        </div>
      </div>
    </div>
  );
}
